import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { useGetSquadDataQuery } from "../../slices/squadDataApiSlice";
import PitchSvg from "../PitchSvg";
import {
  calculateShotExpectedScoreNew,
  calculateVerticalWind,
} from "../../utils/expectedScore";
import { getShotMapCalibration } from "../../utils/shotMapCalibration";

const DEFAULT_SET_PLAYS = [
  "Open Play",
  "Free Kick from Hands",
  "Free Kick from Ground",
  "Penalty",
  "45m Kick",
  "Mark",
];
const DEFAULT_SHOT_TYPES = ["Goal attempt", "2 Point attempt", "Point attempt"];
const DEFAULT_METHODS = ["Left foot", "Right foot", "Hand"];
const DEFAULT_PRESSURES = ["Low", "Medium", "High"];
const DEFAULT_OUTCOMES = [
  "Goal",
  "2 Point",
  "Point",
  "Wide",
  "Post",
  "Short",
  "Blocked",
  "Saved",
  "45",
];

// -- Pixel calibration --
const {
  INNER_LEFT_PX,
  INNER_TOP_PX,
  INNER_RIGHT_PX,
  INNER_BOTTOM_PX,
  X_PCT_OFFSET,
  X_PCT_SCALE,
  Y_PCT_OFFSET,
  Y_PCT_SCALE,
} = getShotMapCalibration();

const INNER_WIDTH_PX = INNER_RIGHT_PX - INNER_LEFT_PX;
const INNER_HEIGHT_PX = INNER_BOTTOM_PX - INNER_TOP_PX;
const formatShotTypeLabel = (value) =>
  String(value || "").replace(/\s*attempt$/i, "");

export default function EditShotModal({
  show,
  onHide,
  onUpdate,
  initialData = {},
  game = {},
  setPlays = DEFAULT_SET_PLAYS,
  shotTypes = DEFAULT_SHOT_TYPES,
  methods = DEFAULT_METHODS,
  pressures = DEFAULT_PRESSURES,
  outcomes = DEFAULT_OUTCOMES,
}) {
  // Teams & squad
  const homeTeam = game?.HomeTeam ?? "";
  const awayTeam = game?.AwayTeam ?? "";

  //Add correct for wind
  const windStrength = Number(game?.WindStrength) || 0;
  const windAngle = Number(game?.WindAngle) || 0;

  const { data: squadData } = useGetSquadDataQuery();
  const firstHalfVideoStartSeconds = Number(game?.FirstHalfVideoTime);
  const secondHalfVideoStartSeconds = Number(game?.SecondHalfVideoTime);
  const hasHalfVideoTimes =
    Number.isFinite(firstHalfVideoStartSeconds) &&
    Number.isFinite(secondHalfVideoStartSeconds);

  // Form state
  const [form, setForm] = useState({
    gameHalf: "1",
    gameTime: "",
    teamShot: homeTeam,
    playerName: "",
    setPlay: "",
    shotType: "",
    shotMethod: "",
    shotPressure: "",
    shotOutcome: "",
    newAttack: "",
    distance: "",
    angle: "",
    side: "",
  });

  // Dot position in SVG coords
  const [dotPos, setDotPos] = useState({ xSvg: null, ySvg: null });
  const [submitError, setSubmitError] = useState("");
  const mapRef = useRef(null);

  const getEnteredVideoTimeSeconds = useCallback((gameHalf, gameTimeSeconds) => {
    const safeGameTime = Number(gameTimeSeconds);
    if (!Number.isFinite(safeGameTime)) return NaN;
    if (String(gameHalf) === "1" && Number.isFinite(firstHalfVideoStartSeconds)) {
      return safeGameTime + firstHalfVideoStartSeconds;
    }
    if (String(gameHalf) === "2" && Number.isFinite(secondHalfVideoStartSeconds)) {
      return safeGameTime + secondHalfVideoStartSeconds;
    }
    if (String(gameHalf) === "3" && Number.isFinite(secondHalfVideoStartSeconds)) {
      return safeGameTime + secondHalfVideoStartSeconds;
    }
    return safeGameTime;
  }, [firstHalfVideoStartSeconds, secondHalfVideoStartSeconds]);

  // When the modal opens (or initialData changes), fill form & reproject the stored shot
  useEffect(() => {
    if (!show || !initialData._id) return;

    setForm({
      gameHalf: String(initialData.gameHalf),
      gameTime: getFormattedTimeString(
        getEnteredVideoTimeSeconds(initialData.gameHalf, initialData.gameTimeSeconds),
      ),
      teamShot: initialData.teamShot || homeTeam,
      playerName: initialData.playerName,
      setPlay: initialData.setPlay,
      shotType: initialData.shotType,
      shotMethod: initialData.shotMethod,
      shotPressure: initialData.shotPressure,
      shotOutcome: initialData.shotOutcome,
      newAttack: initialData.newAttack,
      distance: String(initialData.distance),
      angle: String(initialData.angle),
      side: initialData.side,
    });

    // reverse-project metres ? SVG coords
    const d = Number(initialData.distance);
    const aRad = (Number(initialData.angle) * Math.PI) / 180;
    const dx = (initialData.side === "Left" ? -1 : 1) * d * Math.sin(aRad);
    const dy = d * Math.cos(aRad);

    const xPitchPct = X_PCT_OFFSET + X_PCT_SCALE * dx;
    const yPitchPct = Y_PCT_OFFSET + Y_PCT_SCALE * dy;
    const xSvg = INNER_LEFT_PX + xPitchPct * INNER_WIDTH_PX;
    const ySvg = INNER_TOP_PX + yPitchPct * INNER_HEIGHT_PX;

    setDotPos({ xSvg, ySvg });
    setSubmitError("");
  }, [show, initialData, homeTeam, getEnteredVideoTimeSeconds]);

  // Generic form-field handler
  const handleChange = (e) => {
    let { name, value } = e.target;
    //convert to string if checkbox toggled
    if (name === "newAttack"){
      value = e.target.checked ? "Yes" : "No"
    }

    setForm((f) => ({
      ...f,
      [name]: value,
      ...(name === "teamShot" ? { playerName: "" } : {}),
    }));
    setSubmitError("");
  };

  // Clicking on the pitch: project click ? metres ? form ? replot dot
  const onMapClick = (e) => {
  const rect = mapRef.current.getBoundingClientRect();
  const pctX = (e.clientX - rect.left) / rect.width;
  const pctY = (e.clientY - rect.top) / rect.height;

    const pitchX = pctX;
    const pitchY = pctY;
    const dx = (pitchX - X_PCT_OFFSET) / X_PCT_SCALE;
    const dy = (pitchY - Y_PCT_OFFSET) / Y_PCT_SCALE;

    const dist = Math.hypot(dx, dy);
    const ang = (Math.atan2(Math.abs(dx), dy) * 180) / Math.PI;
    const side = dx < 0 ? "Left" : "Right";

    setForm((f) => ({
      ...f,
      distance: dist.toFixed(2),
      angle: ang.toFixed(0),
      side,
    }));

    const xPitchPct = X_PCT_OFFSET + X_PCT_SCALE * dx;
    const yPitchPct = Y_PCT_OFFSET + Y_PCT_SCALE * dy;
    const xSvg = INNER_LEFT_PX + xPitchPct * INNER_WIDTH_PX;
    const ySvg = INNER_TOP_PX + yPitchPct * INNER_HEIGHT_PX;
    setDotPos({ xSvg, ySvg });
  };

 

  const getTimeInSeconds = value => {
    if (!/^\d{1,2}:[0-5]\d:[0-5]\d$/.test(value || "")) return NaN;
    const [hh, mm, ss] = value.split(":").map(Number)
    if (ss > 59 || mm > 59) {
      return NaN
    } 

    return ((hh * 60 * 60) + (mm * 60) + ss)
  }

  


   function getFormattedTimeString(sec) {
    const total = Number(sec) || 0;

    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;

    return [h, m, s]
      .map(x => String(x).padStart(2, "0"))
      .join(":");
  } 

  const isValidHHMMSS = (value) =>
    /^\d{1,2}:[0-5]\d:[0-5]\d$/.test(String(value || "").trim());

  const getHalfMismatchWarning = (gameHalf, shotTimeSeconds) => {
    if (!hasHalfVideoTimes || !Number.isFinite(shotTimeSeconds)) return "";
    if (
      gameHalf === "1" &&
      (shotTimeSeconds < firstHalfVideoStartSeconds ||
        shotTimeSeconds >= secondHalfVideoStartSeconds)
    ) {
      return "Are you sure? Time does not match 1st half.";
    }
    if (
      (gameHalf === "2" || gameHalf === "3") &&
      shotTimeSeconds < secondHalfVideoStartSeconds
    ) {
      return `Are you sure? Time is before ${gameHalf === "2" ? "2nd" : "3rd"} half start.`;
    }
    return "";
  };

  const getDerivedGameTimeSeconds = (gameHalf, enteredTimeSeconds) => {
    if (!Number.isFinite(enteredTimeSeconds)) return NaN;
    if (String(gameHalf) === "1") {
      if (!Number.isFinite(firstHalfVideoStartSeconds)) return NaN;
      return Math.max(0, enteredTimeSeconds - firstHalfVideoStartSeconds);
    }
    if (String(gameHalf) === "2") {
      if (!Number.isFinite(secondHalfVideoStartSeconds)) return NaN;
      return Math.max(0, enteredTimeSeconds - secondHalfVideoStartSeconds);
    }
    if (String(gameHalf) === "3") {
      if (!Number.isFinite(secondHalfVideoStartSeconds)) return NaN;
      return Math.max(0, enteredTimeSeconds - secondHalfVideoStartSeconds);
    }
    return enteredTimeSeconds;
  };

  // Submit handler
  const handleSubmit = (e) => {
    e.preventDefault();
    const enteredTimeSeconds = getTimeInSeconds(form.gameTime);
    if (String(form.gameHalf) === "1" && !Number.isFinite(firstHalfVideoStartSeconds)) {
      setSubmitError("Set the game's First Half Video Time before editing a 1st-half shot.");
      return;
    }
    if (
      (String(form.gameHalf) === "2" || String(form.gameHalf) === "3") &&
      !Number.isFinite(secondHalfVideoStartSeconds)
    ) {
      setSubmitError(
        `Set the game's Second Half Video Time before editing a ${form.gameHalf === "2" ? "2nd" : "3rd"}-half shot.`
      );
      return;
    }
    const gameTimeSeconds = getDerivedGameTimeSeconds(
      form.gameHalf,
      enteredTimeSeconds,
    );
    if (Number.isNaN(gameTimeSeconds)) {
      setSubmitError("Unable to calculate game time from the entered video time.");
      return;
    }
    setSubmitError("");
    const distNum = Number(form.distance);
    const angNum = Number(form.angle);

    // const vertWind = calculateVerticalWind(
    //   game?.WindStrength,
    //   game?.WindAngle,
    //   angNum,
    // );

    // Determine if direction of play with +1 if teamShot is homeTeam, -1 if awayTeam for first half (or third half)  and opposite for second half. This assumes data entry from the perspective of the home team attacking left-to-right, which is how the video footage is usually recorded.
    const dirShot = (form.teamShot === homeTeam ? 1 : -1) * (form.gameHalf === "2" ? -1 : 1);
    const vertWind = dirShot * windStrength * Math.cos((windAngle * Math.PI) / 180);


    const expectedScore = calculateShotExpectedScoreNew(
      distNum,
      angNum,
      form.shotPressure,
      form.setPlay,
      form.shotMethod,
      form.side,
      form.shotType,
      vertWind,
      game?.GameType || "Mens",
    );
    onUpdate({
      id: initialData._id,
      gameHalf: Number(form.gameHalf),
      gameTimeSeconds,
      teamShot: form.teamShot,
      playerName: form.playerName,
      setPlay: form.setPlay,
      shotType: form.shotType,
      shotMethod: form.shotMethod,
      shotPressure: form.shotPressure,
      shotOutcome: form.shotOutcome,
      newAttack: form.newAttack,
      distance: distNum,
      angle: angNum,
      side: form.side,
      expectedScore,
      gameId: game?._id,
      gameDate: new Date(game.GameDate).getTime(),
      homeTeam,
      awayTeam,
    });
  };

  // Build the player list:
  const playersForTeam = (squadData?.entries || [])
    .filter((p) => p.team === form.teamShot)
    .map((p) => p.playerName);
  const teamHasPlayers = playersForTeam.length > 0;
  const shotTimeSecondsForWarning = isValidHHMMSS(form.gameTime)
    ? getTimeInSeconds(form.gameTime)
    : NaN;
  const halfMismatchWarning = getHalfMismatchWarning(
    form.gameHalf,
    shotTimeSecondsForWarning,
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      dialogClassName="shot-modal-wide"
      centered
      contentClassName="bg-dark text-white shot-modal-content"
    >
      <button
        type="button"
        className="btn-close btn-close-white shot-modal-close"
        aria-label="Close"
        onClick={onHide}
      />
      <Modal.Body as={Row} className="text-white shot-modal-body shot-modal-body--no-header">
        {/* -- FORM SIDE -- */}
        <Col md={5} className="shot-modal-form-col">
          <h2 className="shot-modal-side-title">Edit Shot</h2>
          <Form className="shot-modal-form" noValidate autoComplete="off" onSubmit={handleSubmit}>
            {submitError ? (
              <Alert variant="danger" className="py-2 mb-3">
                {submitError}
              </Alert>
            ) : null}
            <Row className="g-2 align-items-start shot-modal-form-row">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Team</Form.Label>
                  <div>
                    {[homeTeam, awayTeam]
                      .filter(Boolean)
                      .map((team) => (
                        <Form.Check
                          key={team}
                          inline
                          type="radio"
                          id={`edit-shot-team-${team}`}
                          name="teamShot"
                          label={team}
                          value={team}
                          checked={form.teamShot === team}
                          onChange={handleChange}
                          required
                        />
                      ))}
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Game Half</Form.Label>
                  <div className="shot-modal-radio-row shot-modal-radio-row--compact">
                    {["1", "2", "3"].map((half) => (
                      <Form.Check
                        key={half}
                        inline
                        type="radio"
                        id={`edit-shot-half-${half}`}
                        name="gameHalf"
                        label={half}
                        value={half}
                        checked={form.gameHalf === half}
                        onChange={handleChange}
                        required
                      />
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 align-items-start shot-modal-form-row">
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>Video Time (HH:MM:SS)</Form.Label>
                  <Form.Control
                    className="w-50"
                    type="text"
                    name="gameTime"
                    placeholder="00:00:00"
                    value={form.gameTime}
                    onChange={handleChange}
                    pattern="^\\d{1,2}:[0-5]\\d:[0-5]\\d$"
                    required
                  />
                  {halfMismatchWarning && (
                    <Form.Text className="text-warning d-block small">{halfMismatchWarning}</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={7} className="ps-md-2">
                <Form.Group className="mb-3">
                  <Form.Label>Shot Type</Form.Label>
                  <div>
                    {shotTypes.map((st, idx) => (
                      <Form.Check
                        key={st}
                        inline
                        type="radio"
                        id={`edit-shot-type-${idx}`}
                        name="shotType"
                        label={formatShotTypeLabel(st)}
                        value={st}
                        checked={form.shotType === st}
                        onChange={handleChange}
                        required
                      />
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 shot-modal-form-row">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Player</Form.Label>
                  {!teamHasPlayers && form.teamShot && (
                    <Alert variant="warning" className="py-2 mb-2">
                      You cannot save this shot for "{form.teamShot}" because this team has no players.
                    </Alert>
                  )}
                  <Form.Select
                    name="playerName"
                    value={form.playerName}
                    onChange={handleChange}
                    disabled={!teamHasPlayers}
                    required
                  >
                    <option value="">Select player</option>
                    {playersForTeam.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Set Play</Form.Label>
                  <Form.Select
                    name="setPlay"
                    value={form.setPlay}
                    onChange={handleChange}
                    required
                  >
                    {setPlays.map((sp) => (
                      <option key={sp} value={sp}>
                        {sp}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 shot-modal-form-row">
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Method</Form.Label>
                  <div>
                    {methods.map((m, idx) => (
                      <Form.Check
                        key={m}
                        type="radio"
                        id={`edit-shot-method-${idx}`}
                        name="shotMethod"
                        label={m}
                        value={m}
                        checked={form.shotMethod === m}
                        onChange={handleChange}
                        required
                      />
                    ))}
                  </div>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Pressure</Form.Label>
                  <div>
                    {pressures.map((p, idx) => (
                      <Form.Check
                        key={p}
                        type="radio"
                        id={`edit-shot-pressure-${idx}`}
                        name="shotPressure"
                        label={p}
                        value={p}
                        checked={form.shotPressure === p}
                        onChange={handleChange}
                        required
                      />
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 align-items-end shot-modal-form-row">
              <Col md={9}>
                <Form.Group className="mb-3">
                  <Form.Label>Outcome</Form.Label>
                  <Form.Select
                    name="shotOutcome"
                    value={form.shotOutcome}
                    onChange={handleChange}
                    required
                  >
                    {outcomes.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="d-flex flex-column align-items-start mb-3">
                  <Form.Label>New Attack?</Form.Label>
                  <Form.Check
                    type="switch"
                    aria-label="New Attack?"
                    name="newAttack"
                    checked={form.newAttack === "Yes"}
                    value={form.newAttack === "Yes"}
                    onChange={handleChange}
                    className="mt-1"
                    id="newAttackCheckBox"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Button
              type="submit"
              variant="warning"
              className="w-100 text-dark fw-semibold"
              disabled={!teamHasPlayers}
            >
              Update Shot
            </Button>
          </Form>
        </Col>

        {/* -- PITCH & DOT -- */}
        <Col md={7}>
          <div
            className="shot-modal-map"
            ref={mapRef}
            onClick={onMapClick}
            style={{
              position: "relative",
              width: "100%",
              paddingTop: `${(INNER_HEIGHT_PX / INNER_WIDTH_PX) * 100}%`,
            }}
          >
            <PitchSvg
              className="pitch-bg"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                cursor: "crosshair",
              }}
            />
            <svg
              className="pitch-overlay"
              viewBox={`${INNER_LEFT_PX} ${INNER_TOP_PX} ${INNER_WIDTH_PX} ${INNER_HEIGHT_PX}`}
              preserveAspectRatio="xMidYMid meet"
            >
              {dotPos.xSvg != null && (
                <circle
                  cx={dotPos.xSvg}
                  cy={dotPos.ySvg}
                  r="4"
                  fill="#ff1900"
                  stroke="#ff1900"
                />
              )}
            </svg>
          </div>
        </Col>
      </Modal.Body>
    </Modal>
  );
}

